/**
 * **Menú del portal admin por grupos de trabajo** (pedido de Viva, reunión del 11-sep-2026).
 *
 * El menú anterior copiaba las 14 secciones del AF y listaba sus 69 pantallas en la
 * sidebar, todas abiertas: nadie encontraba nada. Ahora la sidebar muestra sólo los grupos,
 * ordenados por el trabajo del día (primero el mostrador, al final la configuración). Al
 * entrar a un grupo se ve una página de tarjetas, una por pantalla, repartidas en bloques.
 *
 * Este archivo decide **dónde vive cada pantalla en el menú**; `screens.ts` sigue siendo el
 * registro de pantallas (ruta, permisos, sección del AF). Las dos cosas se separan a
 * propósito: la sección del AF es trazabilidad de la especificación, el grupo es cómo
 * trabaja la gente. Un test (`groups.test.ts`) verifica que toda pantalla construida y
 * visible esté en exactamente un grupo (más su atajo de `ALSO_IN`, si lo tiene) o en
 * `OUTSIDE_MENU`.
 *
 * El gating sigue siendo cosmético (Arquitectura §3.2): un grupo o una tarjeta que el rol
 * no puede abrir no se dibuja, pero lo que protege es la API.
 */
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Archive,
  ArrowLeftRight,
  Banknote,
  BookUser,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartNoAxesColumn,
  ClipboardCheck,
  Clock,
  Coins,
  CreditCard,
  DoorOpen,
  Dumbbell,
  FileKey2,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  GitMerge,
  GraduationCap,
  HandCoins,
  Handshake,
  HeartHandshake,
  House,
  IdCard,
  Images,
  Inbox,
  Landmark,
  LayoutGrid,
  ListChecks,
  LockKeyhole,
  MailCheck,
  MapPin,
  Megaphone,
  Newspaper,
  Package,
  PackageMinus,
  PackagePlus,
  PartyPopper,
  Percent,
  Plug,
  Power,
  Radio,
  Receipt,
  Rocket,
  ScanLine,
  Scale,
  ScrollText,
  Settings2,
  ShieldAlert,
  ShoppingBasket,
  SlidersHorizontal,
  Store,
  Tablet,
  Tag,
  Target,
  TrendingUp,
  TriangleAlert,
  Trophy,
  Truck,
  UserCheck,
  UserRoundSearch,
  UserPlus,
  UserX,
  Users,
  Wallet,
} from "lucide-react";
import type { UserRole } from "@viva/shared";
import { canSeeScreen } from "./nav.js";
import { SCREENS, type ScreenDef } from "./screens.js";

export type GroupId =
  | "inicio"
  | "mostrador"
  | "socios"
  | "clases"
  | "kiosko"
  | "personal"
  | "administracion"
  | "reportes"
  | "configuracion";

/** Una tarjeta del grupo: apunta a una pantalla del registro y la presenta en criollo. */
export interface CardDef {
  screenId: string;
  /** Nombre corto para la tarjeta. Sin él se usa el título del registro. */
  title?: string;
  /** Qué se hace ahí, en una línea que entienda quien atiende el mostrador. */
  blurb: string;
  icon: LucideIcon;
  /** Las una o dos acciones que más se usan del grupo: van primero y resaltadas. */
  featured?: boolean;
}

export interface BlockDef {
  title: string;
  cards: CardDef[];
}

export interface GroupDef {
  id: GroupId;
  label: string;
  blurb: string;
  icon: LucideIcon;
  blocks: BlockDef[];
}

/** Ruta de la página de tarjetas de un grupo. */
export function hubPath(groupId: GroupId): string {
  return `/admin/seccion/${groupId}`;
}

export const GROUPS: GroupDef[] = [
  {
    id: "inicio",
    label: "Inicio",
    blurb: "Los números del día y cómo viene el mes.",
    icon: House,
    blocks: [
      {
        title: "Tablero",
        cards: [
          {
            screenId: "dashboard",
            title: "Tablero del dueño",
            blurb: "Facturación, asistentes y ventas del día contra el objetivo.",
            icon: ChartNoAxesColumn,
          },
        ],
      },
    ],
  },
  {
    id: "mostrador",
    label: "Mostrador",
    blurb: "Lo de todos los días en recepción: cobrar, la caja, el kiosko y la gente que entra.",
    icon: Store,
    blocks: [
      {
        title: "Cobros y caja",
        cards: [
          {
            screenId: "planes.cobrar",
            title: "Cobrar",
            blurb: "Cuotas, matrícula y planes de un socio, con el precio de la lista vigente.",
            icon: CreditCard,
            featured: true,
          },
          {
            screenId: "caja.turno",
            title: "Caja del turno",
            blurb: "Abrir la caja, ver el efectivo del turno, sobres y movimientos.",
            icon: Wallet,
            featured: true,
          },
          {
            screenId: "caja.cierre",
            title: "Cerrar el turno",
            blurb: "Contar el efectivo a ciegas y entregar la caja.",
            icon: LockKeyhole,
          },
          {
            screenId: "caja.sobres-profes",
            title: "Sobres de profes",
            blurb: "Entregar el sobre de pago con el código que le llega al profe por email.",
            icon: MailCheck,
          },
        ],
      },
      {
        title: "Kiosko",
        cards: [
          {
            // También vive en el grupo Kiosko (ver `ALSO_IN`): recepción la busca acá.
            screenId: "kiosko.consumo-interno",
            title: "Consumo interno",
            blurb:
              "Registrar lo que alguien del equipo se lleva del kiosko sin pagar, a su nombre.",
            icon: PackageMinus,
          },
        ],
      },
      {
        title: "Gente en la sede",
        cards: [
          {
            screenId: "crm.interesados",
            title: "Interesados",
            blurb: "Quien vino a averiguar o a una clase de prueba, y cuándo volver a llamarlo.",
            icon: UserPlus,
          },
          {
            screenId: "acceso.ingresos",
            title: "Ingresos",
            blurb: "Quién entró y quién fue rechazado en el molinete, y por qué.",
            icon: DoorOpen,
          },
          {
            // El registro de ingresos es de encargado para arriba; la recepción trabaja
            // sobre el semáforo del molinete. Los roles superiores ven las dos tarjetas.
            screenId: "kiosko.semaforo",
            title: "Semáforo de ingreso",
            blurb: "Cada lectura del molinete en vivo, con el motivo; paso manual y excepciones.",
            icon: Radio,
          },
          {
            screenId: "socios.visitantes",
            title: "Visitantes SportClub y Wellhub",
            blurb: "Los que entran por una plataforma o un convenio externo.",
            icon: ScanLine,
          },
          {
            screenId: "crm.mensajes",
            title: "Mensajes de la sede",
            blurb: "Lo que escriben los socios desde la app, primero lo sin responder.",
            icon: Inbox,
          },
          {
            screenId: "profesores.mis-comisiones",
            title: "Mis comisiones",
            blurb:
              "Tu objetivo del mes, las ventas de planes que se te atribuyeron y la comisión proyectada.",
            icon: HandCoins,
          },
        ],
      },
    ],
  },
  {
    id: "socios",
    label: "Socios",
    blurb: "El padrón, las altas, los débitos y quién debe.",
    icon: Users,
    blocks: [
      {
        title: "Padrón",
        cards: [
          {
            screenId: "socios.padron",
            title: "Padrón de socios",
            blurb: "Buscar y filtrar socios por estado, apto médico, deuda o convenio.",
            icon: IdCard,
            featured: true,
          },
          {
            screenId: "socios.alta",
            title: "Alta de socio",
            blurb: "Dar de alta en el mostrador, con chequeo de duplicados y firma en la tablet.",
            icon: UserPlus,
            featured: true,
          },
          {
            screenId: "socios.grupos",
            title: "Grupos familiares",
            blurb: "Familias con domicilio compartido, su descuento y quién paga.",
            icon: HeartHandshake,
          },
          {
            screenId: "socios.retiro-emergencia",
            title: "Emergencias médicas",
            blurb: "Registro de un episodio médico en la sede y aviso al contacto de emergencia.",
            icon: TriangleAlert,
          },
          {
            screenId: "socios.duplicados",
            title: "Fusión de duplicados",
            blurb: "Unir dos fichas de la misma persona sin perder su historia.",
            icon: GitMerge,
          },
        ],
      },
      {
        title: "Cuotas y débitos",
        cards: [
          {
            screenId: "planes.debitos",
            title: "Débitos automáticos",
            blurb: "Adheridos, lotes enviados a Payway, rechazos y bajas.",
            icon: CalendarClock,
          },
          {
            screenId: "planes.debitos-archivo",
            title: "Débito por archivo",
            blurb: "Presentaciones a PrismaNet, rendiciones y migración de la planilla.",
            icon: FileSpreadsheet,
          },
          {
            screenId: "planes.deudores",
            title: "Deudores",
            blurb: "Quién tiene cuotas vencidas, desde cuándo y cuánto.",
            icon: UserX,
          },
          {
            screenId: "socios.convenios",
            title: "Convenios, canjes y cortesías",
            blurb: "Convenios con empresas, canjes y cortesías con quién las autorizó.",
            icon: Handshake,
          },
        ],
      },
      {
        title: "Comunicación",
        cards: [
          {
            screenId: "crm.campanas",
            title: "Campañas",
            blurb: "Mails y avisos a la app para un grupo de socios.",
            icon: Megaphone,
          },
          {
            screenId: "crm.moderacion",
            title: "Noticias y moderación",
            blurb: "Noticias para la app y lo que publican los socios en el muro.",
            icon: Newspaper,
          },
        ],
      },
    ],
  },
  {
    id: "clases",
    label: "Clases",
    blurb: "La grilla del mes, la ocupación y los eventos.",
    icon: CalendarRange,
    blocks: [
      {
        title: "Clases",
        cards: [
          {
            screenId: "clases.grilla",
            title: "Grilla del mes",
            blurb: "Qué clase hay, a qué hora, en qué salón y con qué profe.",
            icon: LayoutGrid,
            featured: true,
          },
          {
            // Recepción no edita la grilla: la consulta en sólo lectura para su sede activa.
            screenId: "clases.grilla-consulta",
            title: "Clases de la sede",
            blurb: "La grilla del mes de tu sede, para reservar por un socio o anotar una prueba.",
            icon: CalendarDays,
            featured: true,
          },
          {
            screenId: "clases.ocupacion",
            title: "Ocupación y asistencia",
            blurb: "Cuántos se anotaron, cuántos vinieron y cuántos faltaron.",
            icon: UserCheck,
          },
          {
            screenId: "clases.eventos",
            title: "Eventos",
            blurb: "Masterclass, Viva Games y actividades especiales.",
            icon: PartyPopper,
          },
        ],
      },
    ],
  },
  {
    id: "kiosko",
    label: "Kiosko",
    blurb: "Vender, las ventas del día, el stock, los productos y la tablet pública con QR.",
    icon: ShoppingBasket,
    blocks: [
      {
        title: "Ventas",
        cards: [
          {
            screenId: "contable.pos",
            title: "Vender",
            blurb: "Bebidas y productos, cobrados desde la recepción.",
            icon: ShoppingBasket,
            featured: true,
          },
          {
            screenId: "kiosko.ventas",
            title: "Ventas",
            blurb: "Todo lo vendido, del mostrador y de la tablet pública, con su detalle.",
            icon: Receipt,
            featured: true,
          },
        ],
      },
      {
        title: "Mercadería",
        cards: [
          {
            screenId: "contable.stock",
            title: "Stock y conteo",
            blurb: "Reponer, contar y registrar mermas de los productos.",
            icon: Package,
          },
          {
            screenId: "kiosko.consumo-interno",
            title: "Consumo interno",
            blurb: "Lo que el equipo se lleva sin pagar: baja el stock a nombre de quien retira.",
            icon: PackageMinus,
          },
          {
            screenId: "contable.productos",
            title: "Productos",
            blurb: "Qué se vende, a qué precio y con qué stock mínimo.",
            icon: Tag,
          },
          {
            screenId: "kiosko.proveedores",
            title: "Proveedores",
            blurb: "A quién se le compra cada producto, con su código y su último precio.",
            icon: Truck,
          },
          {
            screenId: "kiosko.compras",
            title: "Compras",
            blurb: "Lo que se le compra a cada proveedor: suma el stock y fija el último costo.",
            icon: PackagePlus,
          },
        ],
      },
      {
        title: "Tablet pública",
        cards: [
          {
            screenId: "kiosko.tablets",
            title: "Tablets públicas",
            blurb: "El link de la tienda para la tablet: el socio compra solo y paga con QR.",
            icon: Tablet,
          },
        ],
      },
    ],
  },
  {
    id: "personal",
    label: "Personal",
    blurb: "El equipo, sus horarios y lo que cobra cada uno.",
    icon: BookUser,
    blocks: [
      {
        title: "Equipo",
        cards: [
          {
            screenId: "profesores.legajos",
            title: "Personal y legajos",
            blurb: "Profes, recepción y encargados: contratación, sede y categoría.",
            icon: BookUser,
            featured: true,
          },
          {
            screenId: "profesores.turnos",
            title: "Turnos y fichajes",
            blurb: "Horarios de cada uno y a qué hora fichó.",
            icon: Clock,
          },
        ],
      },
      {
        title: "Pagos al equipo",
        cards: [
          {
            screenId: "profesores.preliquidacion",
            title: "Liquidaciones del mes",
            blurb: "Lo que se le paga a cada uno este mes, clase por clase.",
            icon: Coins,
            featured: true,
          },
          {
            screenId: "profesores.tarifario",
            title: "Tarifario",
            blurb: "Cuánto vale cada clase según la disciplina y la sede.",
            icon: Tag,
          },
          {
            screenId: "profesores.varas",
            title: "Premios por clase",
            blurb: "La base de alumnos de cada profe, su historia y el premio al superarla.",
            icon: Trophy,
          },
          {
            screenId: "profesores.objetivos",
            title: "Objetivos de recepción",
            blurb: "Metas de venta y comisiones de cada recepcionista.",
            icon: Target,
          },
        ],
      },
    ],
  },
  {
    id: "administracion",
    label: "Administración",
    blurb: "La plata que entró, la que salió, la facturación y los cierres.",
    icon: Landmark,
    blocks: [
      {
        title: "Caja",
        cards: [
          {
            screenId: "caja.historial",
            title: "Cierres y sobres",
            blurb: "Todos los cierres de turno y los sobres de efectivo.",
            icon: Archive,
            featured: true,
          },
          {
            screenId: "caja.diferencias",
            title: "Diferencias y arqueos",
            blurb: "Cada faltante o sobrante, quién lo tuvo y en qué quedó.",
            icon: Scale,
          },
          {
            screenId: "caja.conciliacion",
            title: "Conciliación",
            blurb:
              "Transferencias de Mercado Pago, débitos y lotes de Payway contra lo registrado.",
            icon: ArrowLeftRight,
          },
          {
            screenId: "reportes.cierres",
            title: "Cierres de las sedes",
            blurb: "Cada arqueo: lo declarado contra el sistema y quién cerró.",
            icon: Banknote,
          },
        ],
      },
      {
        title: "Facturación e impuestos",
        cards: [
          {
            screenId: "contable.comprobantes",
            title: "Comprobantes",
            blurb: "Facturas y notas de crédito emitidas en ARCA.",
            icon: Receipt,
          },
          {
            screenId: "contable.emisores",
            title: "Emisores fiscales",
            blurb: "Los CUITs que facturan en ARCA, su certificado y sus puntos de venta.",
            icon: FileKey2,
          },
          {
            screenId: "contable.exportes",
            title: "Exportes contables",
            blurb: "Libro IVA Ventas, gastos, cobros y kiosko para el contador.",
            icon: FileSpreadsheet,
          },
          {
            screenId: "contable.convenios",
            title: "Liquidación de plataformas",
            blurb: "Lo que hay que cobrarles a SportClub y Wellhub por los ingresos.",
            icon: Handshake,
          },
        ],
      },
      {
        title: "Gastos",
        cards: [
          {
            screenId: "contable.egresos",
            title: "Egresos",
            blurb: "Gastos y pagos, con su comprobante y la sede.",
            icon: FileText,
          },
          {
            screenId: "contable.proveedores",
            title: "Proveedores",
            blurb: "A quién se le compra, por rubro y modalidad, y cuánto se le lleva gastado.",
            icon: Truck,
          },
        ],
      },
    ],
  },
  {
    id: "reportes",
    label: "Reportes y alertas",
    blurb: "Lo que el sistema detectó y cómo vienen los números.",
    icon: ChartNoAxesColumn,
    blocks: [
      {
        title: "Alertas y control",
        cards: [
          {
            screenId: "reportes.alertas-negocio",
            title: "Alertas del negocio",
            blurb: "Caídas de asistencia o de recaudación que hay que mirar.",
            icon: TrendingUp,
            featured: true,
          },
          {
            screenId: "roles.alertas-seguridad",
            title: "Alertas de seguridad",
            blurb: "Pagos anulados, descuentos grandes, excepciones y accesos raros.",
            icon: ShieldAlert,
            featured: true,
          },
          {
            screenId: "roles.auditoria",
            title: "Auditoría",
            blurb: "Quién hizo qué, cuándo y desde dónde.",
            icon: ScrollText,
          },
        ],
      },
      {
        title: "Plata",
        cards: [
          {
            screenId: "reportes.recaudacion",
            title: "Recaudación mensual",
            blurb: "Lo cobrado en el mes por sede y por medio de pago.",
            icon: Banknote,
          },
          {
            screenId: "reportes.facturacion",
            title: "Facturación",
            blurb: "Lo facturado por día, sede y concepto.",
            icon: Receipt,
          },
          {
            screenId: "contable.resultado",
            title: "Resultado por sede",
            blurb: "Ingresos menos gastos por mes, sede y actividad.",
            icon: Scale,
          },
        ],
      },
      {
        title: "Socios y clases",
        cards: [
          {
            screenId: "reportes.socios-activos",
            title: "Socios activos",
            blurb: "Cuántos socios activos hay y cómo cambia mes a mes.",
            icon: Users,
          },
          {
            screenId: "reportes.en-riesgo",
            title: "No renovados y en riesgo",
            blurb: "Los que no renovaron o dejaron de venir.",
            icon: UserX,
          },
          {
            screenId: "reportes.asistencia",
            title: "Asistencia",
            blurb: "Presentes por clase, disciplina, horario y sede.",
            icon: Dumbbell,
          },
          {
            screenId: "reportes.objetivos",
            title: "Objetivos por recepcionista",
            blurb: "Ventas de cada recepcionista contra su meta.",
            icon: Target,
          },
        ],
      },
    ],
  },
  {
    id: "configuracion",
    label: "Configuración",
    blurb: "Planes y precios, sedes, usuarios y cómo se comporta el sistema.",
    icon: Settings2,
    blocks: [
      {
        title: "Planes y textos legales",
        cards: [
          {
            screenId: "planes.catalogo",
            title: "Planes y precios",
            blurb:
              "Los planes que se venden hoy con sus precios, el asistente para crear y editar planes, la lista de precios y los flyers.",
            icon: Tag,
            featured: true,
          },
          {
            screenId: "planes.promociones",
            title: "Promociones",
            blurb: "Descuentos por tiempo limitado.",
            icon: Percent,
          },
          {
            screenId: "planes.flyers",
            title: "Flyers de precios",
            blurb: "Los flyers del mes con los precios de la lista, en PNG y PDF.",
            icon: Images,
          },
          {
            screenId: "socios.textos-legales",
            title: "Textos legales",
            blurb: "Reglamento, consentimientos y lo que firma el socio.",
            icon: ScrollText,
          },
        ],
      },
      {
        title: "Sedes y equipos",
        cards: [
          {
            screenId: "config.sedes",
            title: "Sedes y locales",
            blurb: "Las tres sedes, Central y sus locales.",
            icon: Building2,
          },
          {
            screenId: "config.salones",
            title: "Salones",
            blurb: "Los salones de cada sede, su capacidad y su QR.",
            icon: MapPin,
          },
          {
            screenId: "config.disciplinas",
            title: "Disciplinas",
            blurb: "Las actividades, su duración y sus condiciones.",
            icon: Dumbbell,
          },
          {
            screenId: "acceso.puntos",
            title: "Puntos de acceso",
            blurb: "Molinetes y QR de cada local.",
            icon: DoorOpen,
          },
          {
            screenId: "config.dispositivos",
            title: "Dispositivos",
            blurb: "Tablets del kiosko y lectores: vincular, ver si están vivos y dar de baja.",
            icon: Tablet,
          },
          {
            screenId: "config.zkteco",
            title: "Molinetes ZKTeco",
            blurb:
              "El lector de huella de cada molinete: si está andando, sincronizarlo y abrirlo.",
            icon: Fingerprint,
          },
          {
            screenId: "caja.configuracion",
            title: "Cuentas y terminales",
            blurb: "Cuentas bancarias y posnet Payway de cada sede.",
            icon: CreditCard,
          },
        ],
      },
      {
        title: "Usuarios y reglas",
        cards: [
          {
            screenId: "roles.usuarios",
            title: "Usuarios y permisos",
            blurb: "Quién entra al sistema, con qué rol y en qué sedes.",
            icon: UserCheck,
            featured: true,
          },
          {
            screenId: "config.parametros",
            title: "Parámetros",
            blurb: "Plazos, topes y reglas que el sistema aplica solo.",
            icon: SlidersHorizontal,
          },
          {
            screenId: "config.catalogos",
            title: "Catálogos",
            blurb: "Listas de opciones: motivos, orígenes, medios.",
            icon: ListChecks,
          },
          {
            screenId: "config.integraciones",
            title: "Integraciones",
            blurb: "Payway, ARCA, Mercado Pago, mail y demás conexiones.",
            icon: Plug,
          },
        ],
      },
      {
        title: "Sistema",
        cards: [
          {
            screenId: "config.jobs",
            title: "Tareas automáticas",
            blurb: "Lo que el sistema corre solo y cuándo corrió por última vez.",
            icon: Clock,
          },
          {
            screenId: "config.salud",
            title: "Salud del sistema",
            blurb: "Si las conexiones y los servicios están andando.",
            icon: Activity,
          },
        ],
      },
      {
        title: "Puesta en marcha",
        cards: [
          {
            screenId: "migracion.estado",
            title: "Migración de datos",
            blurb: "Corridas de migración de Crossfy, con sus diferencias y errores.",
            icon: Rocket,
          },
          {
            screenId: "migracion.dia",
            title: "Día de migración",
            blurb:
              "Los pasos del día del corte en orden: Excel, huellas del molinete y reporte final.",
            icon: CalendarCheck,
          },
          {
            screenId: "migracion.checklist",
            title: "Checklist de arranque",
            blurb: "Todo lo que tiene que estar listo antes de encender una sede.",
            icon: ClipboardCheck,
          },
          {
            screenId: "migracion.corte",
            title: "Encendido por sede",
            blurb: "El día del corte: pasar cada sede al sistema nuevo.",
            icon: Power,
          },
        ],
      },
      {
        // El curso lo hace todo el staff (RN-CAP-01): por esta tarjeta, Configuración
        // aparece también para la recepción y el contador, con sólo lo que pueden ver.
        title: "Capacitación",
        cards: [
          {
            screenId: "capacitacion.curso",
            title: "Curso de uso del sistema",
            blurb: "Aprendé a usar el sistema paso a paso y consultalo mientras trabajás.",
            icon: GraduationCap,
            featured: true,
          },
          {
            screenId: "capacitacion.equipo",
            title: "Seguimiento del equipo",
            blurb: "Quién hizo el curso, cómo le fue y qué preguntas se le complicaron.",
            icon: UserRoundSearch,
          },
        ],
      },
    ],
  },
];

/**
 * Pantallas construidas que NO van en el menú porque ya tienen su lugar en la barra de
 * arriba: la sede activa (selector de la barra) y la cuenta propia (menú del usuario).
 */
export const OUTSIDE_MENU: readonly string[] = ["roles.sede-activa", "cuenta.mi-cuenta"];

/**
 * Pantallas de detalle (cuelgan de un id, no tienen tarjeta): a qué tarjeta pertenecen.
 * Sirve para marcar el grupo en la sidebar y armar la miga de pan.
 */
export const DETAIL_PARENT: Readonly<Record<string, string>> = {
  "socios.ficha": "socios.padron",
  "planes.cuenta-corriente": "socios.padron",
  "planes.flyers.editor": "planes.flyers",
  "clases.ficha": "clases.grilla",
  "crm.ficha-interesado": "crm.interesados",
  "profesores.detalle-liquidacion": "profesores.preliquidacion",
  "capacitacion.leccion": "capacitacion.curso",
  "capacitacion.final": "capacitacion.curso",
  "capacitacion.certificado": "capacitacion.curso",
};

/**
 * Pantallas que, además de su grupo, tienen una tarjeta de atajo en otro. La pantalla
 * pertenece al grupo de su ruta (miga de pan, sidebar, buscador); el atajo sólo la acerca a
 * quien la busca en otro lado. «Consumo interno» es del Kiosko, y la recepción la tiene a
 * mano en el Mostrador (pedido del 22-oct-2026).
 */
export const ALSO_IN: Readonly<Record<string, GroupId>> = {
  "kiosko.consumo-interno": "mostrador",
};

const SCREEN_BY_ID = new Map(SCREENS.map((screen) => [screen.id, screen]));

export interface VisibleCard extends CardDef {
  screen: ScreenDef;
  to: string;
}

export interface VisibleBlock {
  title: string;
  cards: VisibleCard[];
}

export interface VisibleGroup {
  group: GroupDef;
  blocks: VisibleBlock[];
  /** A dónde lleva la sidebar: la página de tarjetas, o la pantalla si hay una sola. */
  to: string;
  cardCount: number;
}

function visibleCard(card: CardDef, role: UserRole): VisibleCard | null {
  const screen = SCREEN_BY_ID.get(card.screenId);
  if (!screen || screen.status !== "built" || !canSeeScreen(screen, role)) return null;
  return { ...card, screen, to: screen.path };
}

/** Un grupo tal como lo ve un rol, o `null` si no tiene ninguna tarjeta para él. */
export function visibleGroup(group: GroupDef, role: UserRole): VisibleGroup | null {
  const blocks = group.blocks
    .map((block) => ({
      title: block.title,
      cards: block.cards
        .map((card) => visibleCard(card, role))
        .filter((card): card is VisibleCard => card !== null),
    }))
    .filter((block) => block.cards.length > 0);
  const cards = blocks.flatMap((block) => block.cards);
  if (cards.length === 0) return null;
  // Un grupo con una sola tarjeta no necesita página intermedia: se entra directo.
  const to = cards.length === 1 ? cards[0]!.to : hubPath(group.id);
  return { group, blocks, to, cardCount: cards.length };
}

/** La sidebar del portal admin para un rol: sólo los grupos con algo para ver. */
export function menuFor(role: UserRole): VisibleGroup[] {
  return GROUPS.map((group) => visibleGroup(group, role)).filter(
    (group): group is VisibleGroup => group !== null,
  );
}

export function findGroup(groupId: string | undefined): GroupDef | undefined {
  return GROUPS.find((group) => group.id === groupId);
}

/** Grupo al que pertenece una pantalla (o su tarjeta madre, si es de detalle). */
export function groupOfScreen(screenId: string): GroupDef | undefined {
  const cardId = DETAIL_PARENT[screenId] ?? screenId;
  return GROUPS.find(
    (group) =>
      group.id !== ALSO_IN[cardId] &&
      group.blocks.some((block) => block.cards.some((card) => card.screenId === cardId)),
  );
}

/** `/admin/socios/:memberId` → regex que matchea `/admin/socios/<cualquier segmento>`. */
function pathPattern(path: string): RegExp {
  const escaped = path
    .split("/")
    .map((segment) =>
      segment.startsWith(":") ? "[^/]+" : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("/");
  return new RegExp(`^${escaped}/?$`);
}

// Todas las áreas: cada ruta empieza por la suya, así que no se pisan. El botón «¿Cómo se
// usa?» también la necesita en el portal profesores.
const SCREEN_PATTERNS = SCREENS.map((screen) => ({
  screen,
  pattern: pathPattern(screen.path),
  // Una ruta literal gana sobre una con parámetro (`/admin/socios/alta` vs `/:memberId`).
  literal: !screen.path.includes(":"),
}));

/** La pantalla del registro que atiende una URL (de cualquier área). */
export function screenForPath(pathname: string): ScreenDef | undefined {
  const matches = SCREEN_PATTERNS.filter(({ pattern }) => pattern.test(pathname));
  const literal = matches.find((match) => match.literal);
  return (literal ?? matches[0])?.screen;
}

/** Qué grupo marcar en la sidebar para una URL. */
export function groupForPath(pathname: string): GroupDef | undefined {
  const hub = /^\/admin\/seccion\/([^/]+)\/?$/.exec(pathname);
  if (hub) return findGroup(hub[1]);
  const screen = screenForPath(pathname);
  return screen ? groupOfScreen(screen.id) : undefined;
}

/** Minúsculas y sin tildes: «cajá» y «caja» son la misma búsqueda. */
function normalize(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export interface ScreenHit {
  card: VisibleCard;
  group: GroupDef;
}

/**
 * Pantallas del menú que coinciden con lo que se escribió en el buscador de la barra.
 * Busca en el nombre de la tarjeta, el del registro, su descripción y el grupo; cada
 * palabra tiene que aparecer en alguno. Primero las que coinciden por nombre.
 */
export function searchScreens(query: string, role: UserRole, limit = 5): ScreenHit[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const scored: { hit: ScreenHit; score: number }[] = [];
  for (const visible of menuFor(role)) {
    for (const card of visible.blocks.flatMap((block) => block.cards)) {
      // El atajo no se repite en el buscador: la pantalla aparece una vez, en su grupo.
      if (ALSO_IN[card.screenId] === visible.group.id) continue;
      const name = normalize(`${card.title ?? ""} ${card.screen.title}`);
      const all = `${name} ${normalize(card.blurb)} ${normalize(visible.group.label)}`;
      if (!words.every((word) => all.includes(word))) continue;
      const score = words.filter((word) => name.includes(word)).length;
      scored.push({ hit: { card, group: visible.group }, score });
    }
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ hit }) => hit);
}
